"use client";

import { useState } from "react";
import Image from "next/image";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import {
  EnFlag,
  DeFlag,
  Checkmark,
  EsFlag,
  FrFlag,
  NlFlag,
  PtFlag,
} from "./assets";
import { i18nLocales } from "@/lib/i18n/routing";
import { fetchTranslations } from "@/app/services/lokalise/actions/translations";

const localesMeta = (i18nFn: any) => [
  {
    code: "de",
    name: i18nFn("de"),
    iconSrc: DeFlag,
  },
  {
    code: "en",
    name: i18nFn("en"),
    iconSrc: EnFlag,
  },
  {
    code: "es",
    name: i18nFn("es"),
    iconSrc: EsFlag,
  },
  {
    code: "fr",
    name: i18nFn("fr"),
    iconSrc: FrFlag,
  },
  {
    code: "nl",
    name: i18nFn("nl"),
    iconSrc: NlFlag,
  },
  {
    code: "pt",
    name: i18nFn("pt"),
    iconSrc: PtFlag,
  },
];

export default function LangDropdown() {
  const { locale } = useParams<{ locale: string }>();
  const nextPathname = usePathname();
  const nextRouter = useRouter();
  const i18nFnLanguages = useTranslations("languages");
  const i18nFnAlts = useTranslations("imageAlts");

  const [isOpen, setIsOpen] = useState(false);

  const handleLangChange = async (nextLang: i18nLocales) => {
    if (locale === nextLang) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);

    try {
      await fetchTranslations(nextLang);
    } catch (error) {
      console.error("Failed to fetch translations:", error);
    }

    // Using Next's navigation hooks, update the URL with the new locale value, and reload the entire page to ensure all components reflect the new language selection immediately
    const newPathname = nextPathname.replace(`/${locale}`, `/${nextLang}`);
    nextRouter.push(newPathname);
  };

  return (
    <div className="relative inline-flex w-auto">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex cursor-pointer items-center justify-center"
      >
        <p className="mr-1 text-sm uppercase">{locale}</p>

        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className={`${isOpen ? "rotate-0" : "rotate-180"} transform transition-transform duration-300`}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.7708 14.7745C16.6278 14.912 16.4359 14.9871 16.2375 14.9833C16.039 14.9796 15.8502 14.8973 15.7124 14.7545L12.008 10.8268L8.30355 14.7545C8.23621 14.8287 8.15466 14.8886 8.06373 14.9307C7.9728 14.9728 7.87433 14.9962 7.77416 14.9996C7.674 15.003 7.57417 14.9862 7.4806 14.9504C7.38703 14.9145 7.30162 14.8602 7.22942 14.7908C7.15723 14.7213 7.09973 14.6381 7.06032 14.5461C7.02091 14.454 7.0004 14.355 7.00001 14.2549C6.99961 14.1548 7.01933 14.0556 7.05801 13.9633C7.09669 13.8709 7.15353 13.7872 7.22517 13.7172L11.4688 9.22892C11.5386 9.15652 11.6224 9.09894 11.715 9.05961C11.8077 9.02027 11.9073 9 12.008 9C12.1086 9 12.2083 9.02027 12.3009 9.05961C12.3936 9.09894 12.4773 9.15652 12.5472 9.22892L16.7908 13.7172C16.9284 13.8601 17.0036 14.0517 16.9999 14.25C16.9961 14.4482 16.9137 14.6369 16.7708 14.7745Z"
            className="fill-grey-dark-500"
          />
        </svg>
      </button>

      <div
        className={`${isOpen ? "flex" : "hidden"} border-grey-dark-200 absolute top-0 right-0 z-[1000] mt-8 w-auto min-w-52 cursor-default items-center justify-start rounded-md border bg-white p-2 shadow-[0px_20px_25px_-5px_#0000001a,0px_8px_10px_-6px_#0000001a]`}
      >
        <ul className="flex w-full cursor-default flex-col">
          {localesMeta(i18nFnLanguages)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(({ code, name, iconSrc }, idx) => (
              <li key={`${code}-${idx}`}>
                <button
                  className="hover:bg-grey-light-500 active:bg-grey-natural-200 mb-1 inline-flex w-full cursor-pointer items-center justify-between rounded-md px-4 py-2 text-sm last-of-type:mb-0"
                  onClick={() => handleLangChange(code as i18nLocales)}
                >
                  <span className="inline-flex items-center justify-start">
                    <Image
                      src={iconSrc}
                      alt={i18nFnAlts(`countryFlags.${code}`)}
                      className="mr-[14px] size-6"
                    />
                    {name}
                  </span>

                  {code === locale && <Image src={Checkmark} alt="" />}
                </button>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}
